import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { formatDate } from "@/lib/prefs";
import { resolveImageUrl } from "@/components/ImageUpload";
import { coverForPost } from "@/lib/journalCover";
import { chefPortraitFor } from "@/lib/chefAvatar";
import { ChefHat, ArrowLeft, Loader2, Star } from "lucide-react";
import CertifiedBadge from "@/components/CertifiedBadge";

function PublicHeader() {
  return (
    <header className="border-b border-border sticky top-0 z-30 backdrop-blur bg-background/85">
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src="/brand/mark-256.png" alt="CulinaireSpecialist" className="w-9 h-9 rounded-full object-contain" style={{ background: "#0a0806", boxShadow: "0 0 0 1px rgba(232,207,154,0.28)" }} />
          <div>
            <div className="font-display text-lg leading-tight">CulinaireSpecialist</div>
            <div className="label-eyebrow text-[0.55rem]">Journal</div>
          </div>
        </Link>
        <nav className="flex items-center gap-4 text-sm">
          <Link to="/blog" className="text-muted-foreground hover:text-foreground">Journal</Link>
          <Link to="/press" className="text-muted-foreground hover:text-foreground">Press</Link>
          <Link to="/login" className="text-muted-foreground hover:gold">Sign in</Link>
        </nav>
      </div>
    </header>
  );
}

function resolvePhoto(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function AuthorArchive() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await api.get(`/public/authors/${slug}`);
        setData(res);
      } catch (e) {
        setError(e.response?.status === 404 ? "Author not found." : "Could not load.");
      } finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) return <><PublicHeader /><div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div></>;
  if (error || !data) return (
    <>
      <PublicHeader />
      <div className="max-w-2xl mx-auto p-16 text-center">
        <h1 className="font-display text-3xl mb-4">{error || "Not found"}</h1>
        <Link to="/blog" className="text-sm gold hover:underline">← Back to Journal</Link>
      </div>
    </>
  );

  const { contributor, posts } = data;
  const certification = data.certification;

  return (
    <div className="min-h-screen" data-testid="author-archive-page">
      <PublicHeader />
      <main className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-8"><ArrowLeft className="w-4 h-4" /> Journal</Link>

        <div className="glass-card rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row gap-6 items-start" data-testid="author-hero">
          {contributor.photo_url ? (
            <img src={resolvePhoto(contributor.photo_url)} alt={contributor.name} className="w-28 h-28 rounded-full object-cover shrink-0" />
          ) : (
            <img src={chefPortraitFor(contributor)} alt={contributor.name} loading="lazy" className="w-28 h-28 rounded-full object-cover shrink-0" style={{ filter: "saturate(0.9) contrast(1.05)" }} />
          )}
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow gold flex items-center gap-1">
              Executive Chef Contributor {contributor.featured && <Star className="w-3 h-3 fill-primary text-primary" />}
            </div>
            <h1 className="font-display text-3xl md:text-4xl leading-tight mt-2" data-testid="author-name">{contributor.name}</h1>
            <div className="text-base gold mt-1" data-testid="author-title">{contributor.title}</div>
            {certification && (
              <div className="mt-3">
                <CertifiedBadge size="md" certificateNumber={certification.certificate_number} />
              </div>
            )}
            {contributor.bio && <p className="text-muted-foreground mt-4 leading-relaxed max-w-xl text-sm">{contributor.bio}</p>}
          </div>
        </div>

        <section className="mt-12" data-testid="author-posts">
          <h2 className="font-display text-2xl mb-6">Articles by {contributor.name.split(" ")[0]}</h2>
          {posts.length === 0 ? (
            <div className="text-muted-foreground text-sm">No published articles yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {posts.map((p) => {
                const coverSrc = p.hero_image ? resolveImageUrl(p.hero_image) : coverForPost(p);
                return (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group card-hover rounded-2xl glass-card overflow-hidden" data-testid={`author-post-${p.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={coverSrc} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" style={{transitionDuration: "400ms", filter: "saturate(0.92) contrast(1.05)"}} />
                  </div>
                  <div className="p-5">
                    <div className="label-eyebrow gold mb-1.5">{p.tags?.slice(0, 3).join(" • ") || "Article"}</div>
                    <h3 className="font-display text-xl leading-tight mb-2">{p.title}</h3>
                    {p.excerpt && <p className="text-muted-foreground text-xs line-clamp-3">{p.excerpt}</p>}
                    <div className="mt-3 text-[0.65rem] text-muted-foreground">{formatDate(p.published_at || p.created_at)}</div>
                  </div>
                </Link>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
